import React, { useCallback, useEffect, useMemo } from 'react';
import { Container, Grid, Stack, Box, Typography, Button, Card } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import { NOT_AVAILABLE } from '../Global/Constants';
import { isValidValue } from '../../utils/CommonFunction';
import { getBankViewData } from '../../store/Slice/Bank/BankSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useBankSelector from '../../store/Slice/Bank/BankSelector';

// ----------------------------------------------------------------------

const getFieldIcon = (label, defaultIcon) => {
  const l = (label || '').toLowerCase();
  if (l.includes('name') && !l.includes('bank') && !l.includes('company') && !l.includes('dsa')) {
    return <PersonOutlineOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('phone') || l.includes('contact')) {
    return <PhoneOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('loan type') || l.includes('mode') || l.includes('source')) {
    return <GridViewOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('employment')) {
    return <WorkOutlineOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('amount')) {
    return <CurrencyRupeeOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('city') || l.includes('center') || l.includes('branch')) {
    return <LocationCityOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('email')) {
    return <MailOutlineOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('bank') || l.includes('company')) {
    return <AccountBalanceOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('submitted') || l.includes('date')) {
    return <CalendarTodayOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('comment')) {
    return <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('id') || l.includes('file') || l.includes('lan')) {
    return <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  if (l.includes('processing') || l.includes('setting')) {
    return <SettingsOutlinedIcon sx={{ fontSize: 22 }} />;
  }
  return defaultIcon || <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />;
};

const getIconBoxStyle = (label) => {
  const l = (label || '').toLowerCase();
  if (l.includes('phone') || l.includes('contact') || l.includes('person')) {
    return { bgcolor: '#ecfeff', color: '#06b6d4' };
  }
  if (l.includes('amount') || l.includes('payout')) {
    return { bgcolor: '#f0fdf4', color: '#10b981' };
  }
  if (l.includes('loan type') || l.includes('category')) {
    return { bgcolor: '#f5f3ff', color: '#6366f1' };
  }
  if (l.includes('employment')) {
    return { bgcolor: '#ecfeff', color: '#0891b2' };
  }
  if (l.includes('bank') || l.includes('company') || l.includes('city')) {
    return { bgcolor: '#f0f9ff', color: '#0284c7' };
  }
  if (
    l.includes('mode') ||
    l.includes('center') ||
    l.includes('submitted') ||
    l.includes('comment')
  ) {
    return { bgcolor: '#eff6ff', color: '#3b82f6' };
  }
  return { bgcolor: '#f0f9ff', color: '#0284c7' };
};

const getHeaderIcon = (title) => {
  const t = (title || '').toLowerCase();
  if (t.includes('dsa') || t.includes('profile')) {
    return <GroupsOutlinedIcon sx={{ fontSize: 20 }} />;
  }
  if (t.includes('bank') || t.includes('processing')) {
    return <AccountBalanceOutlinedIcon sx={{ fontSize: 20 }} />;
  }
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 19 }} />;
};

function ViewBankops() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const rowId = location.state || {};
  const { LoginData } = useLoginSelector();
  const { BankViewData } = useBankSelector();

  const getBankViewAPi = useCallback(() => {
    dispatch(
      getBankViewData({
        companyId: LoginData?.companyId,
        userId: LoginData?.userId,
        leadId: rowId?.leadId,
        stageId: rowId?.stageId
      })
    );
  }, [LoginData, rowId, dispatch]);

  useEffect(() => {
    getBankViewAPi();
  }, [getBankViewAPi]);

  const sections = useMemo(() => {
    const data = BankViewData || {};
    return [
      {
        title: 'Loan Requirement',
        model: data.bankOpsEntityLoanRequirementResponseModel,
        fields: [
          { label: 'Name', key: 'leadName' },
          { label: 'Phone', key: 'leadPhoneNumber' },
          { label: 'Loan Type', key: 'loanType' },
          { label: 'Employment Type', key: 'employmentType' },
          { label: 'Loan Amount', key: 'loanAmount' },
          { label: 'City', key: 'city' },
          { label: 'Email', key: 'leadEmail' }
        ]
      },
      {
        title: 'Submission Details',
        model: data.bankOpsEntitySubmissionDetailsResponseModel,
        fields: [
          { label: 'Bank Name', key: 'bankName' },
          { label: 'Mode of Submission', key: 'modeOfSubmission' },
          { label: 'Processing Center', key: 'processingCentre' },
          { label: 'Person Name', key: 'personName' },
          { label: 'Submitted On', key: 'submittedOn', isDate: true },
          { label: 'Comments', key: 'comments' }
        ]
      },
      {
        title: 'DSA Details',
        model: data.bankOpsEntityLoginProfileResponseModel,
        fields: [
          { label: 'Company Name', key: 'companyName' },
          { label: 'DSA Name', key: 'dsaName' },
          { label: 'Lan Id / File No.', key: 'lanId' },
          { label: 'Source Type', key: 'sourceType' },
          { label: 'Current Stage', key: 'currentStage' },
          { label: 'Current Status', key: 'currentStatus' },
          { label: 'Source Flag', key: 'sourceFlag' },
          { label: 'Login Profile Id', key: 'loginProfileId' }
        ]
      },
      {
        title: 'Bank Processing Details',
        model: data.bankOpsEntityBankProcessingResponseModel,
        fields: [
          { label: 'Branch Name', key: 'branchName' },
          { label: 'Relationship Manager', key: 'relationshipManager' },
          { label: 'RM Contact Number', key: 'rmContactNumber' },
          { label: 'Employee Contact Number', key: 'employeeContactNumber' }
        ]
      }
    ];
  }, [BankViewData]);

  const currentStatus =
    BankViewData?.bankOpsEntityLoginProfileResponseModel?.currentStatus ||
    BankViewData?.bankOpsEntitySubmissionDetailsResponseModel?.currentStatus ||
    rowId?.logInStatusName ||
    NOT_AVAILABLE;

  return (
    <Page title="Partner Management System || Bankops View">
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8, pt: 2.5 }}>
        <Container maxWidth="xl">
          {/* Top Header Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 3.5, gap: 2 }}
          >
            {/* Left: Back Button & Title */}
            <Stack direction="row" spacing={2.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate('/bankops')}
                sx={{
                  color: '#0284c7',
                  borderColor: '#bae6fd',
                  bgcolor: '#ffffff',
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: '13px',
                  textTransform: 'none',
                  py: 0.9,
                  px: 2,
                  boxShadow: '0 2px 6px rgba(2, 132, 199, 0.06)',
                  '&:hover': {
                    bgcolor: '#f0f9ff',
                    borderColor: '#0284c7'
                  }
                }}
              >
                Back to Application List
              </Button>

              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: '#0f172a',
                    fontSize: { xs: '20px', sm: '24px' },
                    letterSpacing: '-0.3px',
                    lineHeight: 1.2
                  }}
                >
                  Application Progress Report
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.25 }}>
                  View complete application information, documents, and status details.
                </Typography>
              </Box>
            </Stack>

            {/* Right: Breadcrumbs & Current Status */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <BrudCrumbs />
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                  Current Status:
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#fef3c7',
                    color: '#b45309',
                    px: 1.75,
                    py: 0.4,
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    border: '1px solid #fde68a'
                  }}
                >
                  {currentStatus}
                </Box>
              </Stack>
            </Stack>
          </Stack>

          {/* Dynamic Section Cards */}
          <Scrollbar>
            <Stack spacing={3}>
              {sections.map((section, index) => {
                if (!isValidValue(section.model)) return null;

                return (
                  <Card
                    key={index}
                    sx={{
                      borderRadius: '16px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                      p: { xs: 2.5, md: 3 },
                      bgcolor: '#ffffff'
                    }}
                  >
                    {/* Card Header */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '8px',
                            bgcolor: '#0284c7',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {getHeaderIcon(section.title)}
                        </Box>
                        <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                          {section.title}
                        </Typography>
                      </Stack>
                    </Stack>

                    {/* Card Fields */}
                    <Grid container spacing={3}>
                      {section.fields.map((field, fIdx) => {
                        const rawValue = section.model?.[field.key];
                        const displayValue = rawValue || NOT_AVAILABLE;

                        const iconStyle = getIconBoxStyle(field.label);

                        return (
                          <Grid item xs={12} sm={6} md={4} key={fIdx}>
                            <Stack direction="row" spacing={1.75} alignItems="center">
                              <Box
                                sx={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: '12px',
                                  bgcolor: iconStyle.bgcolor,
                                  color: iconStyle.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                {getFieldIcon(field.label)}
                              </Box>
                              <Box>
                                <Typography
                                  sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}
                                >
                                  {field.label}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '14px',
                                    fontWeight: 700,
                                    color: '#0f172a',
                                    mt: 0.2
                                  }}
                                >
                                  {displayValue}
                                </Typography>
                              </Box>
                            </Stack>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Card>
                );
              })}
            </Stack>
          </Scrollbar>
        </Container>
      </Box>
    </Page>
  );
}

export default ViewBankops;
